<template>
  <fieldset id="provider-search-results">
    <ApiLoading v-if="checkSomeObjectPropertiesTrue(apiStatus)" :apiError="apiStatus.fail" class="api-loading" isMemberPortal />

    <template v-else>
      <div class="form-section">
        <div class="form-header">
          <h2 class="h4">Select Provider</h2>
          <p>* indicates required field</p>
        </div>
        <FormField labelText="Select provider">
          <ul class="radio-group">
            <li v-for="provider in refData.providers.slice(0, MAX_PROVIDER_SEARCH_RESULTS)" :key="`${provider.providerId}-${provider.subProviderIdentifierType}-${provider.subProviderIdentifier}`">
              <!-- id and value are composited because sometimes the providerId is not unique in the returned list of providers -->
              <input
                type="radio"
                :id="`${provider.providerId}-${provider.subProviderIdentifierType}-${provider.subProviderIdentifier}`"
                :value="`${provider.providerId}-${provider.subProviderIdentifierType}-${provider.subProviderIdentifier}`"
                name="providerDetails"
                v-model.lazy="v$.formResponse.selectedProvider.$model"
              />
              <label :for="`${provider.providerId}-${provider.subProviderIdentifierType}-${provider.subProviderIdentifier}`">
                <span>{{ provider.providerId }}</span>
                <span>{{ getProviderDisplayName(provider.lastName, provider.firstName, provider.middleName) }}</span>
                <span>{{ titleCase(provider.addressLine1) }}</span>
                <span>{{ titleCase(provider.addressLine2) }}</span>
                <span>{{ titleCase(provider.addressLine3) }}</span>
                <span>{{ getAddressLocalityText(provider.suburb, provider.state, provider.postcode) }}</span>
              </label>
            </li>
          </ul>

          <ApiLoading v-if="refData.providers.length == 0" :apiError="true" class="api-loading" id="no-search-results" isMemberPortal>
            <p>No providers matched your search.</p>
            <p>
              You can still
              <router-link v-if="!isCRM" @click="updateSearchType()" :to="toStep2">search by {{ errorMessageSearchType }}.</router-link>
              <button v-else class="non-button-style" @click="updateSearchType()">search by {{ errorMessageSearchType }}.</button>
            </p>
          </ApiLoading>
          <p>
            If your provider is not listed, try refining your search{{ isCRM ? '.' : '' }}
            <template v-if="!isCRM">
              or
              <router-link v-bind:to="{ name: CONTACT_US }" target="_blank">get in touch</router-link>
              .
            </template>
          </p>
        </FormField>
      </div>
    </template>

    <div v-if="showPageNav" class="form-section">
      <div class="form-page-nav">
        <section>
          <button class="secondary" @click="updatePage(pages.PROVIDER_SEARCH.pageNumber)">Search again</button>
          <button @click="handleNextPage(currentPage + 1)" :disabled="v$.formResponse.$invalid || v$.formResponse.$error">Next</button>
        </section>
      </div>
    </div>
  </fieldset>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { CONTACT_US, BENEFIT_QUOTE_GET_A_BENEFIT, CLAIMS_MAKE_A_CLAIM } from '@/router/routeNames';
import { checkSomeObjectPropertiesTrue } from '@general/js/utils/ObjectUtils';
import GABQnMANCStepsMixins from './GABQnMANC_StepsMixins';

export default {
  setup() { return { v$: useVuelidate() }; },

  name: 'GABQ-step-3',

  mixins: [GABQnMANCStepsMixins],

  data() {
    return {
      CONTACT_US,
      MAX_PROVIDER_SEARCH_RESULTS: 10,
      formResponse: {},
      searchType: {
        providerNumber: 'number',
        providerNameAndLocation: 'name-location',
      },
    };
  },

  computed: {
    errorMessageSearchType() {
      return this.feature.step2.searchType === this.searchType.providerNameAndLocation ? 'provider number' : 'location';
    },

    showPageNav() {
      return !checkSomeObjectPropertiesTrue(this.apiStatus);
    },

    toStep2() {
      if (!this.isClaim) {
        return {
          name: BENEFIT_QUOTE_GET_A_BENEFIT,
          query: {
            step: this.pages.PROVIDER_SEARCH.pageNumber,
          },
        };
      }
      return {
        name: CLAIMS_MAKE_A_CLAIM,
        query: {
          step: this.pages.PROVIDER_SEARCH.pageNumber,
        },
      };
    },
  },

  validations: {
    formResponse: {
      selectedProvider: { required },
    },
  },

  methods: {
    checkSomeObjectPropertiesTrue,

    updateSearchType() {
      if (this.feature.step2.searchType === this.searchType.providerNameAndLocation) {
        this.feature.step2.searchType = this.searchType.providerNumber;
      } else {
        this.feature.step2.searchType = this.searchType.providerNameAndLocation;
      }
      this.updatePage(this.pages.PROVIDER_SEARCH.pageNumber);
    },
  },
};
</script>

<style lang="scss" scoped>
#provider-search-results {
  .radio-group {
    margin-bottom: spacer(1.5);

    li {
      width: 100%;

      label {
        display: block;
        font-weight: $NORMAL;
        text-align: left;

        span {
          display: block;
          &:nth-child(2) {
            font-weight: $BOLD;
          }
        }
      }
    }
  }

  #no-search-results {
    margin-bottom: spacer(1.5);
  }

  .hidden {
    visibility: hidden;
  }
}

.form-header {
  margin-bottom: 48px;

  h2 {
    margin-bottom: 0.25rem;
  }

  p {
    margin-bottom: 0;
  }
}
</style>
